import { createSlice } from "@reduxjs/toolkit";
import { AuthorFinderFacetsType } from "../../types/GetAlertSettings.types";

type Key =
  | "search-term"
  | "data_type"
  | "subject"
  | "author"
  | "searchedAuthor"
  | "journal"
  | "speaker"
  | "searchedSpeaker"
  | "research"
  | "searchedResearch"
  | "researcher"
  | "searchedResearcher"
  | "journalRank"
  | "countryOfPublication"
  | "countryOfPublishingAuthor"
  | "publisher"
  | "article_source"
  | "institutions"
  | "yearFrom"
  | "conference_name"
  | "magazine"
  | "university_name"
  | "sourceIndexType"
  | "inventor"
  | "searchedInventor"
  | "diseases"
  | "drugs"
  | "genes"
  | "study_type";

interface FilterFormat {
  key: string | Key;
  value: string;
}

export type InitialState = {
  loading: boolean;
  sourceType: string[];
  subject: string[];
  diseases: string[];
  drugs: string[];
  genes: string[];
  study_type: string[];
  l3subject: string[];
  author: string[];
  searchedAuthor: string[];
  speaker: string[];
  searchedSpeaker: string[];
  research: string[];
  searchedResearch: string[];
  researcher: string[];
  searchedResearcher: string[];
  inventor: string[];
  searchedInventor: string[];
  journal: string[];
  countryOfPublication: string[];
  publisher: string[];
  article_source: string;
  institutions: string[];
  journalRank: string[];
  countryOfPublishingAuthor: string[];
  sort: string;
  sourceIndex: boolean;
  yearFrom: string | string[];
  monthFrom: string;
  dateFrom: string;
  openAccess: boolean;
  earlyOnline: boolean;
  retractedArticle: boolean;
  datesetAvailability: boolean;
  sourceIndexType: string[];
  abdcGrade: string;
  sjrGrade: string;

  jgateCollection: boolean;
  myLibraryCollection: boolean;
  libraryOpac: boolean;
  myPersonalLibCollection: boolean;
  consortiaCollection: boolean;
  onosCollection: boolean;
  dspace: boolean;
  isOpac_dspace: boolean;

  allAppliedFilter: FilterFormat[];
  collectionValue?: string;

  //for lateral search
  conference_name: string[];
  university_name: string[];
  resourcemaster_id: number[];

  //authorfinderFacets
  authorListFacets: AuthorFinderFacetsType[];
};

const initialState: InitialState = {
  loading: false,
  sourceType: [],
  subject: [],
  diseases: [],
  drugs: [],
  genes: [],
  study_type: [],
  l3subject: [],
  author: [],
  searchedAuthor: [],
  speaker: [],
  searchedSpeaker: [],
  inventor: [],
  searchedInventor: [],
  research: [],
  searchedResearch: [],
  researcher: [],
  searchedResearcher: [],
  journal: [],
  countryOfPublication: [],
  publisher: [],
  article_source: "",
  institutions: [],
  journalRank: [],
  sort: "dateofpublication desc",
  countryOfPublishingAuthor: [],
  sourceIndex: false,
  yearFrom: "",
  monthFrom: "",
  dateFrom: "",
  openAccess: false,
  earlyOnline: false,
  retractedArticle: false,
  datesetAvailability: false,
  sourceIndexType: [],
  abdcGrade: "",
  sjrGrade: "",

  jgateCollection: false,
  myLibraryCollection: false,
  libraryOpac: false,
  myPersonalLibCollection: false,
  consortiaCollection: false,
  onosCollection: false,
  dspace: false,
  isOpac_dspace: false,

  allAppliedFilter: [],
  collectionValue: "",

  conference_name: [],
  university_name: [],
  resourcemaster_id: [],

  authorListFacets: [],
};

export const filterSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    updateSourceType(state, { payload }) {
      state.sourceType = payload;
    },
    updateSubject(state, { payload }) {
      state.subject = payload;
    },

    updateDiseases(state, { payload }) {
      state.diseases = payload;
    },

    updateDrugs(state, { payload }) {
      state.drugs = payload;
    },

    updateGenes(state, { payload }) {
      state.genes = payload;
    },

    updateStudyType(state, { payload }) {
      state.study_type = payload;
    },
    updateSubjectL3(state, { payload }) {
      state.l3subject = payload;
    },
    updateAuthor(state, { payload }) {
      state.author = payload;
    },

    updateSearchedAuthor(state, { payload }) {
      state.searchedAuthor = payload;
    },
    updateSpeaker(state, { payload }) {
      state.speaker = payload;
    },
    updateSearchedSpeaker(state, { payload }) {
      state.searchedSpeaker = payload;
    },
    updateResearch(state, { payload }) {
      state.research = payload;
    },
    updateSearchedResearch(state, { payload }) {
      state.searchedResearch = payload;
    },
    updateResearcher(state, { payload }) {
      state.researcher = payload;
    },
    updateSearchedResearcher(state, { payload }) {
      state.searchedResearcher = payload;
    },
    updateInventor(state, { payload }) {
      state.inventor = payload;
    },
    updateSearchedInventor(state, { payload }) {
      state.searchedInventor = payload;
    },
    updateJournal(state, { payload }) {
      state.journal = payload;
    },
    updateCountryOfPublication(state, { payload }) {
      state.countryOfPublication = payload;
    },
    updatePublisher(state, { payload }) {
      state.publisher = payload;
    },
    updateArticleSource(state, { payload }) {
      state.article_source = payload;
    },
    updateInstitution(state, { payload }) {
      state.institutions = payload;
    },
    updateJournalRank(state, { payload }) {
      state.journalRank = payload;
    },
    updateSort(state, { payload }) {
      state.sort = payload;
    },
    updateCountryOfPublishingAuthor(state, { payload }) {
      state.countryOfPublishingAuthor = payload;
    },

    updateSourceIndex(state, { payload }) {
      state.sourceIndex = payload;
    },
    updateOpenAccess(state, { payload }) {
      state.openAccess = payload;
    },
    updateRetractedArticle(state, { payload }) {
      state.retractedArticle = payload;
    },
    updateDateset(state, { payload }) {
      state.datesetAvailability = payload;
    },

    updateYearFrom(state, { payload }) {
      state.yearFrom = payload;
    },
    updateMonthFrom(state, { payload }) {
      state.monthFrom = payload;
    },
    updateDateFrom(state, { payload }) {
      state.dateFrom = payload;
    },
    updateEarlyOnline(state, { payload }) {
      state.earlyOnline = payload;
    },
    updateSourceIndexType(state, { payload }) {
      state.sourceIndexType = payload;
    },

    updateAbdcGradeArticleLevel(state, { payload }) {
      state.abdcGrade = payload;
    },
    updateSJRGradeArticleLevel(state, { payload }) {
      state.sjrGrade = payload;
    },

    clearPublicationFilter(state) {
      state.yearFrom = "";
      state.dateFrom = "";
      state.monthFrom = "";
      state.earlyOnline = false;
    },

    clearMonthFromFilter(state) {
      state.monthFrom = "";
      state.dateFrom = "";
    },

    // Collection filter
    updateJgateCollection(state, { payload }) {
      state.jgateCollection = payload;
    },

    updateConsortiaCollection(state, { payload }) {
      state.consortiaCollection = payload;
    },

    updateMyLibraryCollection(state, { payload }) {
      state.myLibraryCollection = payload;
    },

    updateOnosCollection(state, { payload }) {
      state.onosCollection = payload;
    },

    updataLibraryOpac(state, { payload }) {
      state.libraryOpac = payload;
    },

    updateDSPACECollection(state, { payload }) {
      state.dspace = payload;
    },

    updateIsOpac_dspace(state, { payload }) {
      state.isOpac_dspace = payload;
    },

    updatePersonalLibrary(state, { payload }) {
      state.myPersonalLibCollection = payload;
    },

    clearl3subject(state) {
      state.l3subject = [];
    },

    clearSJRandABDCFilters(state) {
      state.abdcGrade = "";
      state.sjrGrade = "";
    },

    clearAllFilters(state) {
      state.sourceType = [];
      state.subject = [];
      state.diseases = [];
      state.drugs = [];
      state.genes = [];
      state.study_type = [];
      // state.l3subject=[];
      state.author = [];
      state.speaker = [];
      state.research = [];
      state.researcher = [];
      state.journal = [];
      state.countryOfPublication = [];
      state.publisher = [];
      state.article_source = "";
      state.institutions = [];
      state.journalRank = [];
      state.countryOfPublishingAuthor = [];
      state.retractedArticle = false;
      state.datesetAvailability = false;
      state.sourceIndex = false;
      state.openAccess = false;
      state.yearFrom = "";
      state.conference_name = [];
      state.university_name = [];
      state.resourcemaster_id = [];
      state.inventor = [];
      state.sourceIndexType = [];
      // state.abdcGrade = "";
      // state.sjrGrade = "";

      // For DF-1448

      // state.jgateCollection = false;
      // state.myLibraryCollection = false;
      // state.libraryOpac = false;
      // state.myPersonalLibCollection = false;
      // state.consortiaCollection = false;
      // state.onosCollection = false;
      // state.collectionValue = "";
    },

    clearSearchedAuthor(state) {
      state.searchedAuthor = [];
      state.searchedSpeaker = [];
      state.searchedResearch = [];
      state.searchedResearcher = [];
      state.searchedInventor = [];
    },

    clearSource(state) {
      state.sourceType = [];
    },
    clearSubject(state) {
      state.subject = [];
      // state.l3subject=[];
    },

    clearDiseases(state) {
      state.diseases = [];
      // state.l3subject=[];
    },

    clearDrugs(state) {
      state.drugs = [];
      // state.l3subject=[];
    },

    clearGenes(state) {
      state.genes = [];
      // state.l3subject=[];
    },

    clearStudyType(state) {
      state.study_type = [];
      // state.l3subject=[];
    },

    updateLoadingState(state, { payload }) {
      state.loading = payload;
    },
    clearLimits(state) {
      state.jgateCollection = false;
      state.myLibraryCollection = false;
      state.libraryOpac = false;
      state.myPersonalLibCollection = false;
      state.consortiaCollection = false;
      state.onosCollection = false;
      state.collectionValue = "";
    },

    updateAllFilter(state, { payload }) {
      if (Array.isArray(payload)) {
        state.allAppliedFilter = [...state.allAppliedFilter, ...payload];
      } else {
        state.allAppliedFilter = [...state.allAppliedFilter, payload];
      }
    },
    removeAllFilterType(state, { payload }) {
      state.allAppliedFilter = state.allAppliedFilter.filter(
        (obj) => obj?.value !== payload,
      );
    },
    clearAllFiltersValue(state) {
      state.allAppliedFilter = [];
    },
    spliceAllFilterArray(state) {
      state.allAppliedFilter.splice(1);
    },

    // Function to remove all filters except the searchterm
    removeAppliedFilters(state) {
      state.allAppliedFilter = state.allAppliedFilter.filter(
        (obj) => obj?.key === "search-term",
      );
    },

    updateCollectionValue(state, { payload }) {
      state.collectionValue = payload;
    },

    // for conference lateral search
    updateConfrence(state, { payload }) {
      state.conference_name = payload;
    },
    updateUniversityName(state, { payload }) {
      state.university_name = payload;
    },
    updateResourceId(state, { payload }) {
      state.resourcemaster_id = payload;
    },

    updateauthorListFacets(state, { payload }) {
      state.authorListFacets = payload;
    },
  },
});

export const {
  updateSourceType,
  updateSubject,
  clearSource,
  clearSubject,
  updateLoadingState,
  updateAuthor,
  updateSpeaker,
  updateResearch,
  updateJournal,
  updateSourceIndex,
  updateCountryOfPublication,
  updatePublisher,
  updateArticleSource,
  updateInstitution,
  updateJournalRank,
  updateSort,
  updateCountryOfPublishingAuthor,
  clearAllFilters,
  updateYearFrom,
  updateMonthFrom,
  updateDateFrom,
  updateOpenAccess,
  updateEarlyOnline,
  updateRetractedArticle,
  updateDateset,
  clearPublicationFilter,
  updateJgateCollection,
  updateConsortiaCollection,
  updateMyLibraryCollection,
  updateOnosCollection,
  updataLibraryOpac,
  updatePersonalLibrary,
  clearLimits,
  updateAllFilter,
  removeAllFilterType,
  clearAllFiltersValue,
  removeAppliedFilters,
  updateCollectionValue,
  spliceAllFilterArray,
  updateConfrence,
  updateUniversityName,
  updateResourceId,
  updateResearcher,
  clearMonthFromFilter,
  updateInventor,
  updateDSPACECollection,
  updateIsOpac_dspace,
  updateauthorListFacets,
  updateSubjectL3,
  clearl3subject,
  updateSourceIndexType,
  updateSJRGradeArticleLevel,
  updateAbdcGradeArticleLevel,
  updateDiseases,
  updateDrugs,
  updateGenes,
  updateStudyType,
  clearDiseases,
  clearDrugs,
  clearGenes,
  clearStudyType,
  clearSJRandABDCFilters,
  updateSearchedAuthor,
  updateSearchedSpeaker,
  updateSearchedResearch,
  updateSearchedResearcher,
  updateSearchedInventor,
  clearSearchedAuthor,
} = filterSlice.actions;

export default filterSlice.reducer;
